import React, { useState, useEffect, useCallback, useRef } from 'react';
import { satisfaction as api } from '../services/api';
import SatisfactionQuestionList from './SatisfactionQuestionList';
import SatisfactionSimulation from './SatisfactionSimulation';

const JOURS = [
  { iso: 1, label: 'L' }, { iso: 2, label: 'M' }, { iso: 3, label: 'M' }, { iso: 4, label: 'J' },
  { iso: 5, label: 'V' }, { iso: 6, label: 'S' }, { iso: 7, label: 'D' },
];

const MODES = [
  { cle: 'off', label: 'Off — rien ne part' },
  { cle: 'simulation', label: 'Observation — calculé et consigné, jamais envoyé' },
  { cle: 'actif', label: 'Actif — envoi réel' },
];

const FUSEAUX = [
  { cle: 'America/Martinique', label: 'Antilles (America/Martinique)' },
  { cle: 'Europe/Paris', label: 'Métropole (Europe/Paris)' },
];

// Les sources automatiques ont besoin d'un paramétrage propre ; les deux autres
// n'ont pas de ciblage (envoi déclenché à la main).
const SOURCES_AUTOMATIQUES = ['crm_passages_atelier', 'a97_sellens_ventes_vn'];

export default function SatisfactionFormulaireEditor({ formulaireId, catalogue, user, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [erreur, setErreur] = useState('');
  const [apercu, setApercu] = useState(null);
  const [simulation, setSimulation] = useState(false);
  const compteurCle = useRef(0);
  const isSuperAdmin = user?.role === 'super-admin';

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.getFormulaire(formulaireId);
      setForm(data.formulaire);
      setQuestions(data.questions.map((q) => ({ ...q })));
    } catch (e) {
      setErreur(e.response?.data?.error || 'Chargement impossible');
    } finally {
      setLoading(false);
    }
  }, [formulaireId]);

  useEffect(() => { charger(); }, [charger]);

  const maj = (patch) => setForm((f) => ({ ...f, ...patch }));
  const majParams = (patch) => setForm((f) => ({ ...f, source_params: { ...(f.source_params || {}), ...patch } }));

  const ajouterQuestion = () => {
    compteurCle.current += 1;
    setQuestions((qs) => [...qs, {
      _cle: `neuve-${compteurCle.current}`, libelle: '', type: 'oui_non', obligatoire: true, aide: '',
    }]);
  };

  const enregistrer = async () => {
    if (questions.some((q) => !String(q.libelle || '').trim())) {
      setErreur('Chaque question doit avoir un intitulé.');
      return;
    }
    setSaving(true);
    setErreur('');
    try {
      const { data } = await api.updateFormulaire(form.id, {
        ...form,
        updated_at: form.updated_at,
        questions: questions.map((q) => ({
          id: q.id, libelle: q.libelle, aide: q.aide || null, type: q.type, obligatoire: q.obligatoire !== false,
        })),
      });
      setForm(data.formulaire);
      await charger();
      if (onSaved) onSaved();
    } catch (e) {
      setErreur(e.response?.data?.error || 'Enregistrement impossible');
    } finally {
      setSaving(false);
    }
  };

  const voirApercu = async () => {
    try {
      const { data } = await api.apercu(form.id, {
        modele_sms: form.modele_sms,
        modele_email_objet: form.modele_email_objet,
        modele_email_corps: form.modele_email_corps,
      });
      setApercu(data);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Aperçu impossible');
    }
  };

  // Insertion d'une variable à la fin du modèle SMS : suffisant en pratique et
  // sans piège de curseur (le champ reste librement éditable).
  const insererVariable = (champ, cle) => maj({ [champ]: `${form[champ] || ''}{{${cle}}}` });

  if (loading || !form) {
    return (
      <div className="modal-overlay">
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-body"><div className="sat-vide">Chargement…</div></div>
        </div>
      </div>
    );
  }

  const sourceAutomatique = SOURCES_AUTOMATIQUES.includes(form.source);

  return (
    <>
      <div className="modal-overlay">
        <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>⚙️ {form.nom}</h2>
            <button className="modal-close" onClick={onClose}>✕</button>
          </div>

          <div className="modal-body">
            {erreur && <div className="status-error sat-bandeau">{erreur}</div>}

            {/* ── Identité ── */}
            <div className="sat-section">
              <h3>Formulaire</h3>
              <div className="sat-grille">
                <label className="sat-champ">
                  <span>Nom</span>
                  <input className="sat-input" value={form.nom} onChange={(e) => maj({ nom: e.target.value })} />
                </label>
                <label className="sat-champ">
                  <span>Canal d'envoi</span>
                  <select className="sat-input" value={form.canal} onChange={(e) => maj({ canal: e.target.value })}>
                    <option value="sms">SMS</option>
                    <option value="email">E-mail</option>
                    <option value="sms_email">SMS + e-mail</option>
                  </select>
                </label>
                <label className="sat-champ">
                  <span>Seuil d'invitation Google (NPS)</span>
                  <input className="sat-input" type="number" min="0" max="10"
                    value={form.seuil_google === null ? '' : form.seuil_google}
                    placeholder="vide = aucune invitation"
                    onChange={(e) => maj({ seuil_google: e.target.value === '' ? null : Number(e.target.value) })} />
                </label>
                <label className="sat-champ sat-champ-large">
                  <span>Message d'accueil du questionnaire</span>
                  <input className="sat-input" value={form.message_accueil || ''}
                    placeholder="Votre avis compte pour nous"
                    onChange={(e) => maj({ message_accueil: e.target.value })} />
                </label>
                <label className="sat-champ sat-champ-large">
                  <span>Message de remerciement</span>
                  <input className="sat-input" value={form.message_remerciement || ''}
                    onChange={(e) => maj({ message_remerciement: e.target.value })} />
                </label>
                <label className="sat-check sat-champ">
                  <input type="checkbox" checked={!!form.actif} onChange={(e) => maj({ actif: e.target.checked })} />
                  Formulaire actif
                </label>
              </div>
            </div>

            {/* ── Ciblage ── */}
            <div className="sat-section">
              <h3>Destinataires <small>à qui ce questionnaire part, et quand</small></h3>
              <div className="sat-grille">
                <label className="sat-champ">
                  <span>Source</span>
                  <select className="sat-input" value={form.source} onChange={(e) => maj({ source: e.target.value })}>
                    {(catalogue?.sources || []).map((s) => <option key={s.cle} value={s.cle}>{s.label}</option>)}
                  </select>
                </label>
                {sourceAutomatique && (
                  <>
                    <label className="sat-champ">
                      <span>Envoyer après (jours)</span>
                      <input className="sat-input" type="number" min="0" max="365"
                        value={form.source_params?.delai_jours ?? 3}
                        onChange={(e) => majParams({ delai_jours: Number(e.target.value) })} />
                    </label>
                    <label className="sat-champ">
                      <span>Rattrapage (jours)</span>
                      <input className="sat-input" type="number" min="0" max="365"
                        value={form.source_params?.rattrapage_jours ?? 7}
                        onChange={(e) => majParams({ rattrapage_jours: Number(e.target.value) })} />
                    </label>
                    <label className="sat-champ">
                      <span>Quota par jour</span>
                      <input className="sat-input" type="number" min="1"
                        value={form.source_params?.quota_jour ?? 200}
                        onChange={(e) => majParams({ quota_jour: Number(e.target.value) })} />
                    </label>
                    <label className="sat-champ">
                      <span>Ne pas resolliciter avant (mois)</span>
                      <input className="sat-input" type="number" min="1" max="60"
                        value={form.source_params?.anti_doublon_mois ?? 6}
                        onChange={(e) => majParams({ anti_doublon_mois: Number(e.target.value) })} />
                    </label>
                  </>
                )}
                {form.source === 'crm_passages_atelier' && (
                  <label className="sat-champ sat-champ-large">
                    <span>Codes société Datacar (séparés par des virgules)</span>
                    <input className="sat-input" value={(form.source_params?.codes || []).join(', ')}
                      placeholder="obligatoire — sans code, aucun destinataire"
                      onChange={(e) => majParams({ codes: e.target.value.split(',').map((c) => c.trim()).filter(Boolean) })} />
                  </label>
                )}
                {form.source === 'a97_sellens_ventes_vn' && (
                  <label className="sat-champ sat-champ-large">
                    <span>Codes société SELLENS (vide = les 19)</span>
                    <input className="sat-input" value={(form.source_params?.codsoc || []).join(', ')}
                      placeholder="ex. 001, 004, 010"
                      onChange={(e) => majParams({ codsoc: e.target.value.split(',').map((c) => c.trim()).filter(Boolean) })} />
                  </label>
                )}
              </div>
              {sourceAutomatique && (
                <div className="sat-aide">
                  Délai compté depuis la <b>facturation</b> — la date de vente pour un véhicule, la
                  date de facture pour un passage atelier. Exemple : 20 et 7 sollicitent les clients
                  facturés il y a 20 à 27 jours. Le <b>rattrapage</b> ne crée pas de doublon : un
                  client déjà sollicité ne l'est jamais deux fois. Il évite de perdre ceux dont le
                  jour cible tombe un week-end, ou un jour où le serveur n'a pas tourné — sans lui,
                  avec un envoi du lundi au vendredi, <b>17,6 % des clients ne seraient jamais
                  contactés</b>.
                </div>
              )}
              {sourceAutomatique && (
                <div className="sat-aide">
                  Délai compté depuis la <b>facturation</b> — date de vente pour un véhicule, date de
                  facture pour un passage atelier. Exemple : 20 et 7 sollicitent les clients facturés
                  il y a 20 à 27 jours. Le <b>rattrapage</b> ne crée pas de doublon : un client déjà
                  sollicité ne l'est jamais deux fois. Il évite de perdre ceux dont le jour cible
                  tombe un week-end, ou un jour où le serveur n'a pas tourné — sans lui, avec un
                  envoi du lundi au vendredi, <b>17,6 % des clients ne seraient jamais contactés</b>.
                </div>
              )}
              {form.source === 'a97_sellens_ventes_vn' && (
                <div className="sat-aide">
                  Coordonnées reprises de l'export des ventes VN SELLENS : environ trois acheteurs
                  sur quatre sont joignables. Les autres sont écartés avec le motif « aucun contact
                  connu », visibles dans la simulation.
                </div>
              )}
            </div>

            {/* ── Questions ── */}
            <div className="sat-section">
              <h3>Questions <small>glissez la poignée ⠿ pour changer l'ordre</small></h3>
              <SatisfactionQuestionList
                questions={questions}
                types={catalogue?.types_question || []}
                onChange={setQuestions}
              />
              <button className="btn-secondary" onClick={ajouterQuestion}>+ Ajouter une question</button>
            </div>

            {/* ── Fréquence ── */}
            <div className="sat-section">
              <h3>Fréquence d'envoi</h3>
              <div className="sat-grille">
                <div className="sat-champ sat-champ-large">
                  <span>Jours</span>
                  <div className="sat-jours">
                    {JOURS.map((j) => {
                      const actif = (form.frequence_jours || []).includes(j.iso);
                      return (
                        <button key={j.iso} type="button"
                          className={`filter-chip ${actif ? 'chip-active' : 'chip-default'}`}
                          onClick={() => maj({
                            frequence_jours: actif
                              ? (form.frequence_jours || []).filter((x) => x !== j.iso)
                              : [...(form.frequence_jours || []), j.iso].sort((a, b) => a - b),
                          })}>{j.label}</button>
                      );
                    })}
                  </div>
                </div>
                <label className="sat-champ">
                  <span>Heure</span>
                  <input className="sat-input" type="time"
                    value={String(form.heure_envoi || '10:00').slice(0, 5)}
                    onChange={(e) => maj({ heure_envoi: e.target.value })} />
                </label>
                <label className="sat-champ">
                  <span>Fuseau horaire</span>
                  <select className="sat-input" value={form.timezone} onChange={(e) => maj({ timezone: e.target.value })}>
                    {FUSEAUX.map((f) => <option key={f.cle} value={f.cle}>{f.label}</option>)}
                  </select>
                </label>
                <label className="sat-champ">
                  <span>Mode</span>
                  <select className="sat-input" value={form.mode}
                    disabled={!isSuperAdmin && form.mode !== 'actif'}
                    onChange={(e) => maj({ mode: e.target.value })}>
                    {MODES.map((m) => (
                      <option key={m.cle} value={m.cle} disabled={m.cle === 'actif' && !isSuperAdmin}>{m.label}</option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="sat-aide">
                Trois états et non une case à cocher : c'est le mode « observation » qui rend la mise
                en service sûre — le planificateur calcule tout, consigne ce qui serait parti dans
                l'onglet Réponses, et n'envoie rien. Désactivé par défaut.
              </div>
            </div>

            {/* ── Modèles de message ── */}
            <div className="sat-section">
              <h3>Messages</h3>
              <div className="sat-variables">
                {(catalogue?.variables || []).map((v) => (
                  <button key={v.cle} type="button" className="filter-chip chip-default" title={v.label}
                    onClick={() => insererVariable('modele_sms', v.cle)}>{`{{${v.cle}}}`}</button>
                ))}
              </div>
              <label className="sat-champ sat-champ-large">
                <span>SMS</span>
                <textarea className="sat-input" rows={2} value={form.modele_sms || ''}
                  onChange={(e) => maj({ modele_sms: e.target.value })} />
              </label>
              <label className="sat-champ sat-champ-large">
                <span>Objet de l'e-mail</span>
                <input className="sat-input" value={form.modele_email_objet || ''}
                  onChange={(e) => maj({ modele_email_objet: e.target.value })} />
              </label>
              <label className="sat-champ sat-champ-large">
                <span>Corps de l'e-mail</span>
                <textarea className="sat-input" rows={6} value={form.modele_email_corps || ''}
                  onChange={(e) => maj({ modele_email_corps: e.target.value })} />
              </label>
              <button className="btn-secondary" onClick={voirApercu}>👁️ Aperçu</button>
              {apercu && (
                <div className="sat-apercu">
                  <div><b>SMS ({apercu.sms_longueur}/160)</b><div className="sat-message">{apercu.sms}</div></div>
                  <div><b>Objet</b><div className="sat-message">{apercu.email_objet}</div></div>
                  <div><b>Corps</b><div className="sat-message" dangerouslySetInnerHTML={{ __html: apercu.email_corps }} /></div>
                </div>
              )}
            </div>
          </div>

          <div className="modal-footer">
            {isSuperAdmin && (
              <button className="btn-secondary" onClick={() => setSimulation(true)}>🧪 Simuler</button>
            )}
            <button className="btn-secondary" onClick={onClose}>Annuler</button>
            <button className="btn-primary" onClick={enregistrer} disabled={saving}>
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </div>
      </div>

      {simulation && <SatisfactionSimulation formulaire={form} onClose={() => setSimulation(false)} />}
    </>
  );
}
